/**
 * Emotes (ท่าโพส; see render/emotes.ts). The dances' key positions are in render/dances.ts.
 */
import { keyed, type PoseRigs } from './kit';

/** This group's poses. */
export type Poses =
  'akimbo' | 'fold' | 'think' | 'point' | 'lounge' | 'cheer' | 'clap' | 'laugh' | 'cry' | 'dance' | 'kowtow';

export const POSES: PoseRigs<Poses> = {
  akimbo: () => {
    // Hands on the hips, feet planted wide, chin up.
    return { f1: -4, f2: 4, headDx: -1, arm: 'hip', far: 'hip' };
  },
  fold: () => {
    // Arms folded across the chest, weight on one leg.
    return { f1: -2, f2: 2, lean: -1, arm: 'fold', far: 'fold' };
  },
  think: () => {
    // A hand to the chin, the other arm across the body under its elbow, head bowed a little.
    return { f1: -2, f2: 1, tilt: 4, headDx: 1, arm: 'chin', far: 'fold' };
  },
  point: () => {
    // Stepped in, finger levelled at someone, the other hand on the hip.
    return { o: 1, lean: 1, tilt: 8, f1: -5, f2: 4, arm: 'thrust', far: 'hip', face: 'atk' };
  },
  lounge: () => {
    // Sat on the ground leaning back on one hand, at ease.
    return { o: 12, lean: -2, tilt: -16, headDx: -1, arm: 'hip', far: 'back' };
  },
  cheer: (ph) => {
    // Both arms thrown up, hopping (the second key is the hop).
    return {
      f1: -2,
      f2: 2,
      far: 'up',
      face: 'atk',
      ...keyed(
        [
          { o: 2, tilt: 0, l1: 0, l2: 0, hem: 0, sway: 0.5, arm: 'raise' },
          { o: 0, tilt: -6, l1: 2, l2: 2, hem: 2, sway: 0, arm: 'overhead' },
        ],
        ph,
      ),
    };
  },
  clap: (ph) => {
    // Hands apart and together before the chest.
    return {
      f1: -2,
      f2: 1,
      ...keyed(
        [
          { o: 0, arm: 'clapOpen', far: 'clapOpen' },
          { o: 1, arm: 'clap', far: 'clap' },
        ],
        ph,
      ),
    };
  },
  laugh: (ph) => {
    // Hands on the hips, head thrown back, shaking with it.
    return {
      ...keyed(
        [
          { o: 0, headDx: -1 },
          { o: 1, headDx: -2 },
          { o: 0, headDx: -1 },
          { o: 2, headDx: -2 },
        ],
        ph,
      ),
      lean: -1,
      tilt: -10,
      f1: -3,
      f2: 3,
      arm: 'hip',
      far: 'hip',
      face: 'atk',
      closed: true,
    };
  },
  cry: (ph) => {
    // Hands over the face, shoulders shaking.
    return {
      ...keyed([{ o: 2 }, { o: 3 }], ph),
      lean: 1,
      tilt: 12,
      headDx: 1,
      f1: -1,
      f2: 1,
      arm: 'face',
      far: 'face',
      closed: true,
    };
  },
  dance: (ph) => {
    // Stepping side to side, arms flowing up and out in turn.
    return {
      hem: 1,
      ...keyed(
        [
          { o: 1, lean: 2, tilt: 8, f1: -4, f2: 1, l1: 0, l2: 0, sway: 1.5, arm: 'out', far: 'up' },
          { o: 0, lean: 0, tilt: 0, f1: -2, f2: 3, l1: 2, l2: 0, sway: 0, arm: 'raise', far: 'back' },
          { o: 1, lean: -2, tilt: -8, f1: -1, f2: 4, l1: 0, l2: 0, sway: -1.5, arm: 'out', far: 'up' },
          { o: 0, lean: 0, tilt: 0, f1: -3, f2: 2, l1: 0, l2: 2, sway: 0, arm: 'overhead', far: 'fwd' },
        ],
        ph,
      ),
    };
  },
  kowtow: (ph) => {
    // Fist in palm, upright a moment, then bowing deep and held there.
    return {
      headDx: 1,
      f1: -1,
      f2: 1,
      arm: 'clasp',
      far: 'clasp',
      ...keyed(
        [
          { o: 1, lean: 0, tilt: 8, closed: false },
          { o: 1, lean: 0, tilt: 8, closed: false },
          { o: 5, lean: 1, tilt: 30, closed: true },
          { o: 5, lean: 1, tilt: 30, closed: true },
        ],
        ph,
      ),
    };
  },
};
